import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CircularProgress,
  IconButton,
  Stack,
  Table as MuiTable,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TableBody,
  Button,
  Dialog,
} from '@mui/material';
import { ArrowBackIosNew } from '@mui/icons-material';

import { groupsAtom } from '@/interfaces/site';
import { groupSensorsAtom, selectedGroupIdAtom } from '@/interfaces/group';
import { groupMembersAtom } from '@/interfaces/member';
import ManageDevicesDialog from '@/components/setting/groups/ManageDevicesDialog';
import ManageMembersDialog from '@/components/setting/groups/ManageMembersDialog';

import type { SxProps, Theme } from '@mui/material';

export default function GroupDetailPage() {
  const { data: groups } = useAtomValue(groupsAtom);
  const { data: devices, refetch: refetchDevices } = useAtomValue(groupSensorsAtom);
  const { data: members, refetch: refetchMembers } = useAtomValue(groupMembersAtom);
  const setSelectedGroupId = useSetAtom(selectedGroupIdAtom);

  const [dialog, setDialog] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const isLoading = !(members && devices);
  const groupId = searchParams.get('id') ?? '';
  const group = groups?.find((group) => group.id === groupId);

  const membersTableHeaders = useMemo(() => [t('Common.label.name'), t('Settings.label.email')], [t]);
  const membersTableRows = useMemo(() => {
    if (!members) return [];
    return members.map((member) => [member.info?.name, member.email]);
  }, [members]);
  const devicesTableHeaders = useMemo(() => [t('Common.label.name'), t('Devices.label.serialNumber')], [t]);
  const devicesTableRows = useMemo(() => {
    if (!devices) return [];
    return devices.map((device) => [device.name, device.info.serialNumber]);
  }, [devices]);

  useEffect(() => {
    if (groupId === '') return;
    setSelectedGroupId(groupId);
  }, [groupId, setSelectedGroupId]);

  function onBackClick() {
    navigate('/setting/groups');
  }
  const onDialogClose = useCallback(() => {
    setDialog('');
    refetchDevices();
    refetchMembers();
  }, [refetchDevices, refetchMembers]);

  if (groupId === '' || !group) return <Navigate to="/setting/groups" />;
  return (
    <Stack gap={3} sx={{ height: '100%' }}>
      <Stack direction="row" alignItems="center" gap={1}>
        <IconButton onClick={onBackClick}>
          <ArrowBackIosNew />
        </IconButton>
        <Typography variant="h5" fontWeight="bold" sx={{ mb: '-2px' }}>
          {group.name}
        </Typography>
      </Stack>

      {isLoading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ flexGrow: 1 }}>
          <CircularProgress />
        </Stack>
      ) : (
        <>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="h5" fontWeight="bold">
              {t('Common.label.members')}
            </Typography>
            <Button variant="contained" onClick={() => setDialog('MANAGE_MEMBERS')}>
              {t('Settings.label.manageMembers')}
            </Button>
          </Stack>
          <Table headers={membersTableHeaders} rows={membersTableRows} />

          <Stack direction="row" justifyContent="space-between">
            <Typography variant="h5" fontWeight="bold">
              {t('Common.label.devices')}
            </Typography>
            <Button variant="contained" onClick={() => setDialog('MANAGE_DEVICES')}>
              {t('Common.label.manageDevices')}
            </Button>
          </Stack>
          <Table headers={devicesTableHeaders} rows={devicesTableRows} />
        </>
      )}

      <Dialog open={dialog === 'MANAGE_MEMBERS'} onClose={onDialogClose}>
        <ManageMembersDialog groupName={group?.name} onClose={onDialogClose} />
      </Dialog>
      <Dialog open={dialog === 'MANAGE_DEVICES'} onClose={onDialogClose}>
        <ManageDevicesDialog groupName={group?.name} onClose={onDialogClose} />
      </Dialog>
    </Stack>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  const sxProps: SxProps<Theme> = (theme) => ({
    '& th': { fontSize: '16px', fontWeight: 'bold' },
    '& th, td': {
      borderRight: `1px solid ${theme.palette.divider}`,
      '&:last-of-type': { borderRight: 'none' },
    },
  });

  return (
    <TableContainer sx={sxProps}>
      <MuiTable>
        <TableHead>
          <TableRow>
            {headers.map((header) => (
              <TableCell key={header}>{header}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={`${i}${row[0]}`}>
              {row.map((col, j) => (
                <TableCell key={`${j}${col}`}>{col}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </MuiTable>
    </TableContainer>
  );
}
